import type { ExcalidrawTextElement, FontFamilyValues } from '../element/types';
import { FONT_FAMILY, HAND_DRAWN_FONT_NAME } from './constants';

export interface FontMetrics {
  /** head.unitsPerEm metric */
  unitsPerEm: number;
  /** hhea.ascender metric */
  ascender: number;
  /** hhea.descender metric */
  descender: number;
  /** harcoded unitless line-height, https://github.com/excalidraw/excalidraw/pull/6360#issuecomment-1477635971 */
  lineHeight: number;
}

/**
 * The CSS font stacks the board draws with, set by the host from its own
 * design tokens (`setFontStacks`). The defaults only apply where no host
 * configured them (e.g. the main process, which never measures text).
 */
const fontStacks = { sans: 'sans-serif', serif: 'serif', mono: 'monospace' };
const measuredMetrics = new Map<string, FontMetrics>();

export const setFontStacks = (stacks: {
  sans: string;
  serif?: string;
  mono: string;
}) => {
  fontStacks.sans = stacks.sans;
  fontStacks.serif = stacks.serif || 'serif';
  fontStacks.mono = stacks.mono;
  measuredMetrics.clear();
};

type FontFace = 'sans' | 'serif' | 'mono' | 'handDrawn';

const faceOf = (fontFamily: number): FontFace => {
  switch (fontFamily) {
    case FONT_FAMILY.Serif:
      return 'serif';
    case FONT_FAMILY.Excalifont:
    case FONT_FAMILY.Virgil:
      return 'handDrawn';
    case FONT_FAMILY.Mono:
    case FONT_FAMILY['Comic Shanns']:
      return 'mono';
    default:
      return 'sans';
  }
};

/** A family name, not a CSS declaration or a list of fallback families. */
export const normalizeDrawingFontFace = (value: string | undefined) =>
  value
    ?.trim()
    .replace(/[\r\n\0]/g, '')
    .slice(0, 200) ?? '';

/** CSS `font-family` for a stored preset and optional explicit local family. */
export const getFontFamilyCSS = (
  fontFamily: number,
  fontFace?: string
): string => {
  const face = normalizeDrawingFontFace(fontFace);
  if (face) return `${JSON.stringify(face)}, ${getFontFamilyCSS(fontFamily)}`;
  switch (faceOf(fontFamily)) {
    case 'serif':
      return fontStacks.serif;
    case 'handDrawn':
      // CJK and other scripts Excalifont lacks fall back to the app's sans
      return `${HAND_DRAWN_FONT_NAME}, ${fontStacks.sans}`;
    case 'mono':
      return fontStacks.mono;
    default:
      return fontStacks.sans;
  }
};

// Excalifont's own hhea metrics (upstream FONT_METADATA)
const HAND_DRAWN_METRICS: FontMetrics = {
  unitsPerEm: 1000,
  ascender: 886,
  descender: -374,
  lineHeight: 1.25,
};

// used where the system stacks cannot be measured (no canvas)
const SYSTEM_FALLBACK_METRICS: FontMetrics = {
  unitsPerEm: 2048,
  ascender: 1577,
  descender: -471,
  lineHeight: 1.25,
};

/**
 * The system stacks resolve to different fonts per platform, so their
 * ascent / descent are measured once from the font the browser picked.
 */
const measureStack = (stack: string, lineHeight: number): FontMetrics => {
  const cached = measuredMetrics.get(stack);
  if (cached) {
    return cached;
  }
  let metrics: FontMetrics = { ...SYSTEM_FALLBACK_METRICS, lineHeight };
  if (typeof document !== 'undefined') {
    const context = document.createElement('canvas').getContext('2d');
    if (context) {
      context.font = `100px ${stack}`;
      const sample = context.measureText('Mg中');
      if (sample.fontBoundingBoxAscent) {
        metrics = {
          unitsPerEm: 100,
          ascender: sample.fontBoundingBoxAscent,
          descender: -sample.fontBoundingBoxDescent,
          lineHeight,
        };
      }
    }
  }
  measuredMetrics.set(stack, metrics);
  return metrics;
};

export const getFontMetrics = (
  fontFamily: number,
  fontFace?: string
): FontMetrics => {
  if (normalizeDrawingFontFace(fontFace))
    return measureStack(getFontFamilyCSS(fontFamily, fontFace), 1.25);
  switch (faceOf(fontFamily)) {
    case 'serif':
      return measureStack(fontStacks.serif, 1.25);
    case 'handDrawn':
      return HAND_DRAWN_METRICS;
    case 'mono':
      return measureStack(fontStacks.mono, 1.2);
    default:
      return measureStack(fontStacks.sans, 1.25);
  }
};

/**
 * Calculates vertical offset for a text with alphabetic baseline.
 */
export const getVerticalOffset = (
  fontFamily: ExcalidrawTextElement['fontFamily'],
  fontSize: ExcalidrawTextElement['fontSize'],
  lineHeightPx: number,
  fontFace?: string
) => {
  const { unitsPerEm, ascender, descender } = getFontMetrics(
    fontFamily,
    fontFace
  );

  const fontSizeEm = fontSize / unitsPerEm;
  const lineGap =
    (lineHeightPx - fontSizeEm * ascender + fontSizeEm * descender) / 2;

  const verticalOffset = fontSizeEm * ascender + lineGap;
  return verticalOffset;
};

/**
 * Gets line height for a selected family.
 */
export const getLineHeight = (fontFamily: FontFamilyValues) => {
  const { lineHeight } = getFontMetrics(fontFamily);

  return lineHeight as ExcalidrawTextElement['lineHeight'];
};
